import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { MapPin, Monitor, Search, FileText, SlidersHorizontal } from 'lucide-react'
import { getSectionKinds, listDevices } from '../api/client'
import { useLocations } from '../api/hooks/organization'
import { cn } from '../design/cn'
import { useDebouncedValue } from '../design/useDebouncedValue'
import { paletteResults, serverSearchTerm, settingsForPalette, type PaletteResult } from './commandPalette'
import { isHeading, isSection, visibleNavigation, type NavLinkItem } from './navigation'
import { usePermissions } from './PermissionsContext'
import { useActiveBrand } from './useActiveBrand'

/**
 * Search everything from anywhere: Ctrl+K (⌘K on a Mac), or "/" when not typing.
 *
 * A technician on the phone with "store 14, register 2" types "14 reg 2" and presses Enter: an online
 * device with a screen opens its remote desktop; anything else opens its page. Stores open their
 * devices, and every page the operator can reach is here by name, so the sidebar never has to be
 * scanned (Plans/experience-review-2026-10-10.md, Step 2).
 */

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

function usePaletteShortcut(setOpen: (update: (open: boolean) => boolean) => void) {
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((open) => !open)
      } else if (event.key === '/' && !isTyping(event.target)) {
        event.preventDefault()
        setOpen(() => true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [setOpen])
}

/**
 * Whether the one-time "there is a search here" line has been seen.
 *
 * `localStorage`, read in a try/catch: a private window, cleared site data or a browser that refuses
 * storage must leave the portal working, and the worst case is somebody seeing the hint twice. It is
 * deliberately not a server-side preference — a hint is a per-browser convenience, not a setting.
 */
const HINT_SEEN = 'relay.palette.hint-seen'

function hintAlreadySeen(): boolean {
  try {
    return window.localStorage.getItem(HINT_SEEN) === 'yes'
  } catch {
    return false
  }
}

function rememberHintSeen(): void {
  try {
    window.localStorage.setItem(HINT_SEEN, 'yes')
  } catch {
    // Nothing to do: the hint comes back next time, which is better than a thrown error here.
  }
}

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  usePaletteShortcut(setOpen)
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)
  // The hint the reviewer's complaint asks for: "challenging to figure out where the info I need
  // is". The palette is the answer to that and was invisible unless somebody already knew the
  // shortcut (Plans/esper-ux-parity.md, Phase 7). Shown once, then never again.
  const [showHint, setShowHint] = useState(() => !hintAlreadySeen())

  const dismissHint = () => {
    setShowHint(false)
    rememberHintSeen()
  }

  return (
    <>
      <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search sites, devices and pages"
        aria-keyshortcuts="Control+K"
        className="flex h-8 min-w-0 items-center gap-sm rounded-md border border-input-border bg-input-bg px-sm text-sm text-fg-muted transition-colors hover:border-line-strong hover:text-fg-secondary sm:w-72 md:w-96"
      >
        <Search size={15} aria-hidden="true" className="shrink-0" />
        <span className="hidden truncate sm:inline">Search devices, sites, settings, pages</span>
        <kbd className="ml-auto hidden rounded border border-input-border px-xs font-mono text-xs sm:inline">
          {isMac ? '⌘K' : 'Ctrl K'}
        </kbd>
      </button>
      {showHint ? (
        <div
          role="note"
          className="absolute top-full right-0 z-30 mt-xs w-72 rounded-md border border-line-strong bg-card p-md text-sm shadow-lg"
        >
          <p className="text-fg">
            Press <kbd className="rounded border border-input-border px-xs font-mono text-xs">{isMac ? '⌘K' : 'Ctrl K'}</kbd>{' '}
            anywhere to find a device, a site, a setting or a page.
          </p>
          <button type="button" onClick={dismissHint} className="mt-sm text-accent hover:underline">
            Got it
          </button>
        </div>
      ) : null}
      </div>
      {open ? (
        <PaletteDialog
          onClose={() => {
            setOpen(false)
            // Opening it IS learning it: a hint about a thing somebody has just used is noise.
            if (showHint) dismissHint()
          }}
        />
      ) : null}
    </>
  )
}

const ICON: Record<PaletteResult['kind'], typeof Monitor> = {
  device: Monitor,
  store: MapPin,
  setting: SlidersHorizontal,
  page: FileText,
}
const GROUP: Record<PaletteResult['kind'], string> = {
  device: 'Devices',
  store: 'Sites',
  setting: 'Settings',
  page: 'Pages',
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { hasPermission, hasBrandWidePermission } = usePermissions()
  const { brandId } = useActiveBrand()
  const listId = useId()

  useEffect(() => input.current?.focus(), [])

  const term = useDebouncedValue(serverSearchTerm(query), 150)
  const devices = useQuery({
    queryKey: ['devices', 'palette', brandId ?? null, term],
    queryFn: ({ signal }) =>
      listDevices({ page: 1, limit: 50, search: term, brandId: brandId ?? undefined }, signal),
    enabled: term.length > 0,
    retry: false,
    staleTime: 10_000,
  })
  const stores = useLocations()

  const pages = useMemo<NavLinkItem[]>(() => {
    const visible = visibleNavigation(hasPermission, undefined, hasBrandWidePermission)
    return visible.flatMap((entry) =>
      isHeading(entry) ? [] : isSection(entry) ? entry.items : [entry],
    )
  }, [hasPermission, hasBrandWidePermission])

  // The settings catalogue, from the relay's own schema, so a setting the agent learns is a setting
  // the palette finds with no page to update (Plans/esper-ux-parity.md, Phase 7).
  const schema = useQuery({
    queryKey: ['section-kinds'],
    queryFn: getSectionKinds,
    enabled: hasPermission('policy.view'),
    retry: false,
    staleTime: 5 * 60_000,
  })
  const settings = useMemo(
    () => settingsForPalette(schema.data?.kinds ?? []),
    [schema.data],
  )

  const results = paletteResults(query, {
    devices: devices.data?.devices,
    stores: stores.data?.locations,
    settings,
    pages,
  })
  const current = Math.min(active, Math.max(0, results.length - 1))

  const back = encodeURIComponent(pathname + search)
  const run = (result: PaletteResult, details = false) => {
    onClose()
    if (result.kind === 'device') {
      navigate(
        result.canConnect && !details
          ? `/connect/video/${result.id}?returnUrl=${back}`
          : `/devices/${result.id}`,
      )
    } else if (result.kind === 'store') {
      navigate(`/devices?location=${result.id}`)
    } else {
      navigate(result.to)
    }
  }

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive(results.length ? (current + 1) % results.length : 0)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(results.length ? (current - 1 + results.length) % results.length : 0)
    } else if (event.key === 'Enter' && results[current]) {
      event.preventDefault()
      run(results[current], event.shiftKey)
    }
  }

  const optionId = (index: number) => `${listId}-option-${index}`
  const searching = term.length > 0 && devices.isFetching

  return (
    <div
      className="fixed inset-0 flex items-start justify-center bg-overlay px-md pt-[12vh]"
      style={{ zIndex: 'var(--z-modal)' }}
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="w-full max-w-[40rem] overflow-hidden rounded-lg border border-card-border bg-card shadow-xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-sm border-b border-card-border px-md">
          <Search size={16} aria-hidden="true" className="shrink-0 text-fg-muted" />
          <input
            ref={input}
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls={listId}
            aria-activedescendant={results.length > 0 ? optionId(current) : undefined}
            aria-autocomplete="list"
            aria-label="Search sites, devices and pages"
            placeholder="Device, site, setting or page — e.g. 14 register 2, or brightness"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setActive(0)
            }}
            onKeyDown={onKey}
            className="h-12 flex-1 bg-transparent text-md text-fg outline-none placeholder:text-input-placeholder"
          />
          <kbd className="rounded border border-input-border px-xs font-mono text-xs text-fg-muted">
            Esc
          </kbd>
        </div>

        <ul
          id={listId}
          role="listbox"
          aria-label="Results"
          className="max-h-[60vh] overflow-y-auto p-xs"
        >
          {results.map((result, index) => {
            const Icon = ICON[result.kind]
            const first = index === 0 || results[index - 1].kind !== result.kind
            return (
              <li
                key={`${result.kind}-${'id' in result ? result.id : result.to}`}
                role="presentation"
              >
                {first ? (
                  <div
                    role="presentation"
                    className="px-md pb-xs pt-sm text-xs font-medium text-fg-muted"
                  >
                    {GROUP[result.kind]}
                  </div>
                ) : null}
                <div
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === current}
                  onMouseMove={() => setActive(index)}
                  onClick={() => run(result)}
                  className={cn(
                    'flex cursor-pointer items-center gap-md rounded-md px-md py-sm',
                    index === current ? 'bg-hover' : undefined,
                  )}
                >
                  <span className="relative shrink-0 text-fg-muted">
                    <Icon size={16} aria-hidden="true" />
                    {result.kind === 'device' ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute -bottom-[2px] -right-[2px] h-[7px] w-[7px] rounded-full ring-2 ring-card',
                          result.online ? 'bg-success' : 'bg-fg-muted',
                        )}
                      />
                    ) : null}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm text-fg">{result.title}</span>
                    <span className="truncate text-xs text-fg-secondary">{result.detail}</span>
                  </span>
                  {index === current ? (
                    <span className="shrink-0 text-xs text-fg-muted">
                      {result.kind === 'device' && result.canConnect
                        ? 'Enter to connect · Shift+Enter for details'
                        : 'Enter to open'}
                    </span>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>

        {query.trim() && results.length === 0 ? (
          <p className="px-lg py-lg text-sm text-fg-secondary">
            {searching ? 'Searching…' : `Nothing matches “${query.trim()}”.`}
          </p>
        ) : null}
        {!query.trim() ? (
          <p className="px-lg py-lg text-sm text-fg-secondary">
            Type a site, a device or a page. Every word has to match, in any order:{' '}
            <span className="font-mono">14 reg 2</span> finds Register 2 at site 14.
          </p>
        ) : null}
      </div>
    </div>
  )
}
